import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { inr, fmtDate, statusColor } from "@/lib/utils-format";
import { Badge } from "@/components/ui/badge";
import { DeferredChart } from "../components/DeferredChart";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Sparkles, Loader2, Download, Trophy, Users as UsersIcon, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DiscountOverridesPanel from "@/pages/DiscountOverridesPanel";

export default function ReportsPage() {
  const [sales, setSales] = useState({ invoices: [], count: 0, total: 0 });
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [salespersonPeriod, setSalespersonPeriod] = useState("month");
  const [salesperson, setSalesperson] = useState(null);

  useEffect(() => {
    api.get("/reports/sales").then((r) => setSales(r.data));
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    api.get(`/reports/salesperson?period=${salespersonPeriod}`).then((r) => setSalesperson(r.data)).catch(() => {});
  }, [salespersonPeriod]);

  const runSummary = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reports/ai-summary");
      setSummary(data.summary);
      toast.success("AI summary ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!sales.invoices.length) return toast.error("No invoices to export");
    const header = ["Number", "Customer", "Status", "PaymentStatus", "Subtotal", "GST", "GrandTotal", "AmountPaid", "Date"];
    const rows = sales.invoices.map((i) => [
      i.number, i.customer_snapshot?.name || "", i.status, i.payment_status,
      i.subtotal, i.total_gst, i.grand_total, i.amount_paid || 0, i.created_at,
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bakemill-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Insights</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Reports & Analytics</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="reports-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          <Button onClick={runSummary} disabled={loading} data-testid="reports-ai-btn" className="bg-[#2C1810] hover:bg-[#3a2418] text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            AI Summary
          </Button>
        </div>
      </div>

      {summary && (
        <div className="border border-[#F6AD55] bg-[#FEEBC8]/50 rounded-lg p-5" data-testid="reports-ai-summary">
          <div className="uc-label !text-[#7B341E] flex items-center gap-2 mb-2"><Sparkles className="w-3 h-3" /> AI Executive Summary</div>
          <p className="text-sm text-[#2C1810] leading-relaxed">{summary}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatBlock label="Total Invoices" value={sales.count} />
        <StatBlock label="Total Billed" value={inr(sales.total)} accent="text-[#C05621]" />
        <StatBlock label="Total Collected" value={inr(stats?.total_collected || 0)} accent="text-emerald-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-[#EAE5D9]">
          <CardHeader><CardTitle className="font-[Outfit] text-lg">Revenue Trend</CardTitle></CardHeader>
          <CardContent className="h-72">
            {stats && (
              <DeferredChart height="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.revenue_series}>
                    <CartesianGrid stroke="#EAE5D9" strokeDasharray="3 3" />
                    <XAxis dataKey="month" stroke="#8B7355" fontSize={12} />
                    <YAxis stroke="#8B7355" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EAE5D9", borderRadius: 6 }} formatter={(v) => inr(v)} />
                    <Line type="monotone" dataKey="revenue" stroke="#C05621" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </DeferredChart>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#EAE5D9]">
          <CardHeader><CardTitle className="font-[Outfit] text-lg">Lead Funnel</CardTitle></CardHeader>
          <CardContent className="h-72">
            {stats && (
              <DeferredChart height="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.lead_funnel}>
                    <CartesianGrid stroke="#EAE5D9" strokeDasharray="3 3" />
                    <XAxis dataKey="status" stroke="#8B7355" fontSize={12} />
                    <YAxis stroke="#8B7355" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EAE5D9", borderRadius: 6 }} />
                    <Bar dataKey="count" fill="#C05621" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </DeferredChart>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Salesperson performance */}
      <Card className="border-[#EAE5D9]" data-testid="salesperson-report">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="font-[Outfit] text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#C05621]" /> Salesperson Performance
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={salespersonPeriod} onValueChange={setSalespersonPeriod}>
              <SelectTrigger data-testid="salesperson-period" className="h-9 w-36 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This week</SelectItem>
                <SelectItem value="month">This month</SelectItem>
                <SelectItem value="year">This year</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!salesperson ? (
            <div className="p-8 text-center text-sm text-[#8B7355]">Loading…</div>
          ) : salesperson.salespersons?.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#8B7355]"><UsersIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />No sales recorded in this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left uc-label border-b border-[#EAE5D9]">
                    <th className="px-4 py-3">Rank</th>
                    <th className="px-4 py-3">Salesperson</th>
                    <th className="px-4 py-3">Role · Branch</th>
                    <th className="px-4 py-3 text-right">POS Sales</th>
                    <th className="px-4 py-3 text-right">B2B Sales</th>
                    <th className="px-4 py-3 text-right">Total Sales</th>
                    <th className="px-4 py-3 text-right">Txns</th>
                    <th className="px-4 py-3 text-right">Avg Ticket</th>
                    <th className="px-4 py-3 text-right">Units</th>
                  </tr>
                </thead>
                <tbody>
                  {salesperson.salespersons.map((p, idx) => {
                    const top = salesperson.salespersons[0].sales || 1;
                    const pct = Math.round((p.sales / top) * 100);
                    return (
                      <tr key={p.user_id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`salesperson-row-${p.user_id}`}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${idx === 0 ? "bg-[#FEEBC8] text-[#C05621]" : idx === 1 ? "bg-slate-200 text-slate-700" : idx === 2 ? "bg-amber-100 text-amber-700" : "bg-[#F7F2E8] text-[#8B7355]"}`}>
                            {idx + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium">
                          <div>{p.name}</div>
                          <div className="w-32 h-1 rounded-full bg-[#F7F2E8] mt-1.5 overflow-hidden">
                            <div className="h-full bg-[#C05621]" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#5C4033]">
                          <span className="capitalize">{p.role?.replace("_", " ")}</span>
                          {p.branch_name && <div className="text-[#8B7355]">{p.branch_name}</div>}
                        </td>
                        <td className="px-4 py-3 text-right">{inr(p.pos_sales)}</td>
                        <td className="px-4 py-3 text-right">{inr(p.b2b_sales)}</td>
                        <td className="px-4 py-3 text-right font-semibold">{inr(p.sales)}</td>
                        <td className="px-4 py-3 text-right">{p.txn_count}</td>
                        <td className="px-4 py-3 text-right">{inr(p.avg_ticket)}</td>
                        <td className="px-4 py-3 text-right">{Math.round(p.products_sold)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#F7F2E8] font-semibold text-sm">
                    <td colSpan={5} className="px-4 py-3 text-right">Total</td>
                    <td className="px-4 py-3 text-right">{inr(salesperson.total_sales)}</td>
                    <td className="px-4 py-3 text-right">{salesperson.total_txns}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <DiscountOverridesPanel />

      <Card className="border-[#EAE5D9]">
        <CardHeader><CardTitle className="font-[Outfit] text-lg">All Invoices</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uc-label border-b border-[#EAE5D9]">
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {sales.invoices.map((i) => (
                  <tr key={i.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`report-invoice-${i.id}`}>
                    <td className="px-4 py-3 font-medium">{i.number}</td>
                    <td className="px-4 py-3">{i.customer_snapshot?.name}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(i.status)}`}>{i.status}</Badge></td>
                    <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(i.payment_status)}`}>{i.payment_status}</Badge></td>
                    <td className="px-4 py-3 text-right font-semibold">{inr(i.grand_total)}</td>
                    <td className="px-4 py-3 text-right text-emerald-700">{inr(i.amount_paid || 0)}</td>
                    <td className="px-4 py-3 text-[#8B7355]">{fmtDate(i.created_at)}</td>
                  </tr>
                ))}
                {sales.invoices.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]">No invoices yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const StatBlock = ({ label, value, accent }) => (
  <div className="bg-white border border-[#EAE5D9] rounded-lg p-5">
    <div className="uc-label">{label}</div>
    <div className={`font-[Outfit] text-3xl font-semibold mt-1 tracking-tight ${accent || ""}`}>{value}</div>
  </div>
);
